import React, { useState } from 'react';
import MyInputSwitch from 'components/controls/MyInputSwitch';
import { useToggleToolVersionMutation } from 'services/api/adminToolsQueriesAndMutations';
import styled, { css } from 'styled-components';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { PatchedToolForAdminModel } from 'types/admin-tools/PatchedToolForAdminModel';
import { ToolForAdminModelVersion } from 'types/admin-tools/ToolForAdminModelVersion';
import { ToolVersionState } from 'types/tools/ToolVersionState';
import ConfirmWithCommentDialog, { ConfirmWithCommentPayload } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { HelpBox } from 'components/v2/help-boxes';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import Tooltip from 'components/controls/Tooltip';

interface Props {
  toolData: PatchedToolForAdminModel;
  versionData: ToolForAdminModelVersion;
  hasErrors: boolean;
}

const AdminEditToolVersionToggleSection: React.FC<Props> = ({ toolData, versionData, hasErrors }) => {
  const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState<boolean>(false);
  const isEnabled = versionData.versionState === ToolVersionState.ENABLED;
  const toggleToolVersionMutation = useToggleToolVersionMutation(toolData.id, versionData.ord);

  const handleConfirm = (payload: ConfirmWithCommentPayload | null) => {
    if (!payload) {
      setIsConfirmDialogVisible(false);
      return;
    }

    toggleToolVersionMutation.mutateAsync({ enable: !isEnabled, message: payload.commentText });
    setIsConfirmDialogVisible(false);
  };

  const isSwitcherDisabled = hasErrors && !isEnabled;

  return (
    <SectionWrapper className="w-full">
      <HeaderCaptionSemiBold>Disable / Enable Tool version</HeaderCaptionSemiBold>

      <TextBox $variant={isEnabled ? 'blue' : 'green'}>
        <span>
          {
            "When enabled, this tool version will be available for all companies in the Compiler's EPD tool selection (old wizard).\nIf a tool version is disabled, it is not visible in the user interface drop-down list."
          }
        </span>
        <span>
          <MyInputSwitch
            checked={isEnabled}
            onChange={() => setIsConfirmDialogVisible(true)}
            disabled={isSwitcherDisabled}
          />
          {isSwitcherDisabled && <Tooltip position="title">Please resolve all errors before enabling the version</Tooltip>}
        </span>
      </TextBox>

      {versionData.firstActiveAt && (
        <HelpBox>
          Tool Version was enabled for the first time on {formatDateTimeVerbose(new Date(versionData.firstActiveAt))}.
        </HelpBox>
      )}

      <ConfirmWithCommentDialog
        header="Disable / Enable the tool version"
        help={
          <HelpBox>
            Please note, if you disable a Pre-verified EPD/LCA tool, then it is not visible in the user interface drop-down
            list.
            <br />
            If the tool is a fully pre-verified tool, the EPDs can no longer be updated or created with it.
          </HelpBox>
        }
        inputLabelText="Comment"
        primaryButtonText="Submit"
        isOpened={isConfirmDialogVisible}
        onClose={handleConfirm}
        maxLength={255}
        closable={false}
        isCommentRequired
      />

      {toggleToolVersionMutation.isPending && <OverlaySpinner />}
    </SectionWrapper>
  );
};

export default AdminEditToolVersionToggleSection;

function formatDateTimeVerbose(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeStyle: 'long' }).format(date);
}

const SectionWrapper = styled.div`
  display: grid;
  gap: 16px;
`;

const TextBox = styled.div<{ $variant: 'blue' | 'green' }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  gap: 40px;
  border-radius: 4px;
  white-space: pre-wrap;
  ${(props) => props.theme.typography.bodyBody14Regular};
  ${(props) =>
    props.$variant === 'blue' &&
    css`
      background-color: ${(props) => props.theme.colors.elementsFilledInput};
    `}
  ${(props) =>
    props.$variant === 'green' &&
    css`
      background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
      color: ${(props) => props.theme.colors.primaryGreen};
    `}
`;
